import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useWebSocketContext } from '@/context/websocket-context'

interface UseIwanResult<T = any> {
  data: T | null
  isLoading: boolean
  error: Error | unknown
  isSuccess: boolean
}

const normalizeChainType = (chainType: unknown) =>
  typeof chainType === 'string' ? chainType.toUpperCase() : chainType

const normalizeIwanParams = (method: string, params: unknown) => {
  if (!Array.isArray(params)) return params

  if (method === 'getSlotCount' || method === 'getSlotTime') {
    return {
      chainType: normalizeChainType(params[0]),
    }
  }

  return params
}

export const useIwan = <T = any>(
  funName: Array<string>,
  params: unknown = {},
  enabled: boolean = true,
): UseIwanResult<T> => {
  const { client, status, error: connectionError, call } = useWebSocketContext()
  const method = funName[0]
  const normalizedParams = useMemo(
    () => normalizeIwanParams(method, params),
    [method, params],
  )

  const queryEnabled = Boolean(client) && status !== 'unconfigured' && enabled

  const { data, isLoading, error, isSuccess } = useQuery({
    queryKey: [...funName, normalizedParams, enabled],
    queryFn: async (): Promise<T | null> =>
      call<T>(method, normalizedParams).catch((requestError) => {
        throw requestError instanceof Error
          ? requestError
          : new Error('iWan request failed')
      }),
    enabled: queryEnabled,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
  })

  return useMemo(
    () => ({
      data: data || null,
      isLoading,
      error: error || connectionError,
      isSuccess,
    }),
    [data, isLoading, error, connectionError, isSuccess],
  )
}
